import ts from 'typescript';
import { SANDBOX_COMPILER_OPTIONS } from '../compilerConfig';
import { resolveModulePath } from '../moduleResolver';
import { evaluateModule } from '../transpiler';
import { InvalidProjectPathError, normalizeProjectPath } from './paths';

/** A module a project file imports that is neither in the tree nor provided as a package. */
export class ProjectModuleNotFoundError extends Error {
  constructor(readonly specifier: string, readonly importer: string | null) {
    super(importer ? `Cannot find module "${specifier}" imported by ${importer}` : `Cannot find module "${specifier}"`);
    this.name = 'ProjectModuleNotFoundError';
  }
}

/** A project file that failed to compile or threw while it loaded — named, with what it threw as the cause. */
export class ProjectLoadError extends Error {
  constructor(readonly path: string, readonly cause: unknown) {
    super(`${path} failed to load: ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = 'ProjectLoadError';
  }
}

export interface ProjectLoaderOptions {
  /** Packages the project imports by name: `@gotti/*`, and the browser's stand-ins for Node built-ins. */
  externalModules: ReadonlyMap<string, unknown>;
  console?: Console;
}

/** The view a transpiled default import reads: an ES namespace without `__esModule` gets it (see `compileAndLoad`). */
const interop = (mod: any): any => (mod && mod.__esModule ? mod : { ...mod, __esModule: true });

/** The files the loader runs as script, through TypeScript. A `.json` file is read as its data; nothing else is imported. */
const SCRIPT = /\.(?:[cm]?[jt]s|[jt]sx)$/;

/**
 * Loads modules from a project tree (`path → source`, project-relative: `src/client/systems/A.ts`) the way the project
 * runs under its own tooling, unlike the v1 loader (`compileAndLoad`): `@/` is `src/` (tsconfig's `@/*`), a file is
 * one module whichever spelling imports it, a missing module throws — so a dynamic `import()` of one rejects — and a
 * file that fails to compile or throws while loading throws, naming itself. Only what is imported is evaluated.
 */
export class ProjectLoader {
  private readonly files = new Map<string, string>();
  private readonly cache = new Map<string, { exports: any }>();
  private readonly packages = new Map<string, unknown>();

  constructor(tree: Record<string, string>, private readonly options: ProjectLoaderOptions) {
    for (const [path, code] of Object.entries(tree)) {
      const key = `/${normalizeProjectPath(path)}`;
      // Two spellings of one file would leave the loader to pick one silently; the tree is malformed instead.
      if (this.files.has(key)) throw new InvalidProjectPathError(path, 'two files of the project land on it');
      this.files.set(key, code);
    }
  }

  /** The module at `path` (project-relative; `./` or a leading `/` is fine), evaluated on first load only. */
  load(path: string): Record<string, unknown> {
    return this.require(`/${normalizeProjectPath(path)}`, null);
  }

  /** Every file evaluated so far, project-relative, in the order evaluation began. */
  loadedFiles(): string[] {
    return [...this.cache.keys()].map((key) => key.slice(1));
  }

  /** The tree key a resolved specifier names: itself, or with `.ts`, `.tsx` or `/index.ts`. */
  private fileFor(specifier: string): string | undefined {
    for (const candidate of [specifier, `${specifier}.ts`, `${specifier}.tsx`, `${specifier}/index.ts`]) {
      if (this.files.has(candidate)) return candidate;
    }
    return undefined;
  }

  private require(specifier: string, importer: string | null): any {
    if (this.options.externalModules.has(specifier)) {
      if (!this.packages.has(specifier)) this.packages.set(specifier, interop(this.options.externalModules.get(specifier)));
      return this.packages.get(specifier);
    }
    const key = this.fileFor(specifier);
    if (!key) throw new ProjectModuleNotFoundError(specifier, importer);
    const cached = this.cache.get(key);
    if (cached) return cached.exports;
    // Cached before it runs, so a circular import sees the partial exports — as Node's CommonJS loader does.
    const module = { exports: {} as any };
    this.cache.set(key, module);
    try {
      this.evaluate(key, module);
    } catch (err) {
      this.cache.delete(key);
      // The innermost failure names the file at fault; the files that imported it only pass it on.
      if (err instanceof ProjectLoadError || err instanceof ProjectModuleNotFoundError) throw err;
      throw new ProjectLoadError(key.slice(1), err);
    }
    return module.exports;
  }

  /**
   * Runs one file as `module`: a `.json` file as its data, a script once its syntax checks, anything else refused. The
   * syntax check is the one the CLI's bundler makes: TypeScript's emitter repairs a missing brace or parenthesis and
   * emits code that runs, so its syntactic diagnostics are read rather than trusting the emit.
   */
  private evaluate(key: string, module: { exports: any }): void {
    const path = key.slice(1);
    const code = this.files.get(key)!;
    if (key.endsWith('.json')) {
      module.exports = JSON.parse(code);
      return;
    }
    if (!SCRIPT.test(key)) throw new Error('the site’s preview imports only script and JSON files');
    const output = ts.transpileModule(code, { compilerOptions: { ...SANDBOX_COMPILER_OPTIONS }, fileName: key, reportDiagnostics: true });
    const syntaxError = output.diagnostics?.find((d) => d.category === ts.DiagnosticCategory.Error && d.file && d.start !== undefined);
    if (syntaxError) {
      const { line, character } = syntaxError.file!.getLineAndCharacterOfPosition(syntaxError.start!);
      throw new SyntaxError(`${ts.flattenDiagnosticMessageText(syntaxError.messageText, '\n')} (${path}:${line + 1}:${character + 1})`);
    }
    const requireFn = (importPath: string) => this.require(resolveModulePath(key, importPath, '/src/'), path);
    evaluateModule(key, code, module, requireFn, this.options.console ?? console, { compiled: output.outputText });
  }
}

export interface LoadedPlaygroundProject {
  /** `playground.config.ts` as `gotti playground` resolves it (`resolveConfig`). */
  config: Record<string, unknown>;
  /** The definition the site serves: its tree path and evaluated module (its default export is the definition or pair). */
  definition: { path: string; module: Record<string, unknown> };
  loader: ProjectLoader;
}

/**
 * Loads a project's playground config and the one definition the site serves (`sandbox.playground.ts` unless told
 * otherwise), and only what those import — `gotti.config.ts`, `vite.config.ts` and the web client entry never run.
 * Resolving every playground a config lists (globs, inline entries) is the Tests panel's job, not the preview's.
 */
export async function loadPlaygroundProject(
  tree: Record<string, string>,
  options: ProjectLoaderOptions & { configPath?: string; definitionPath?: string },
): Promise<LoadedPlaygroundProject> {
  const loader = new ProjectLoader(tree, options);
  const config = await loadPlaygroundConfig(loader, options.configPath);
  const definitionPath = normalizeProjectPath(options.definitionPath ?? 'sandbox.playground.ts');
  return { config, definition: { path: definitionPath, module: loader.load(definitionPath) }, loader };
}

/**
 * A project's playground config (`playground.config.ts` unless told otherwise), loaded through `loader` and resolved as
 * `gotti playground` resolves it (`resolveConfig`): what the Tests panel lists every playground from.
 */
export async function loadPlaygroundConfig(loader: ProjectLoader, configPath = 'playground.config.ts'): Promise<Record<string, unknown>> {
  const path = normalizeProjectPath(configPath);
  return resolveConfig(loader.load(path), path);
}

/**
 * A config module as `gotti playground` reads it (the playground's `resolvePlaygroundModule`): its default export — a
 * function (`definePlaygroundConfig`'s function form, or an async one) called with `{ command: 'serve', mode:
 * 'development' }` and awaited — unwrapped of a `default` again, and refused unless it lists `playgrounds`, as the CLI
 * refuses it.
 */
async function resolveConfig(module: Record<string, unknown>, configPath: string): Promise<Record<string, unknown>> {
  const unwrap = (value: unknown): unknown =>
    value && typeof value === 'object' && 'default' in value ? (value as { default: unknown }).default : value;
  let config = unwrap(module);
  if (typeof config === 'function') {
    try {
      config = unwrap(await config({ command: 'serve', mode: 'development' }));
    } catch (err) {
      throw new ProjectLoadError(configPath, err);
    }
  }
  if (!config || typeof config !== 'object' || !(config as Record<string, unknown>).playgrounds) {
    throw new ProjectLoadError(configPath, new Error('its default export lists no playgrounds (`playgrounds`)'));
  }
  return config as Record<string, unknown>;
}
